import { cn } from '@/lib/utils';

export const fieldClass =
  'w-full rounded-md border border-input bg-card px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * Label + control + always-mounted error region (screen readers only register
 * live regions present at first paint — the element stays mounted and the
 * text toggles). Browser-native `required` is intentionally avoided in favor
 * of aria-required so server-side zod validation owns the UX.
 */
export function FormField({
  id,
  name,
  label,
  type = 'text',
  required = false,
  error,
  as = 'input',
  children,
  className,
  defaultValue,
}: {
  id: string;
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  error?: string;
  as?: 'input' | 'textarea' | 'select';
  children?: React.ReactNode;
  className?: string;
  /** Initial value, for a field prefilled from the URL (e.g. the role a job page links with). */
  defaultValue?: string;
}) {
  const describedBy = `${id}-error`;
  const controlProps = {
    id,
    name,
    'aria-required': required || undefined,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    defaultValue,
    className: cn(fieldClass, as === 'textarea' && 'min-h-32 resize-y', className),
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required && (
          <span aria-hidden="true" className="text-primary">
            {' '}
            *
          </span>
        )}
      </label>
      {as === 'textarea' ? (
        <textarea {...controlProps} />
      ) : as === 'select' ? (
        <select {...controlProps}>{children}</select>
      ) : (
        <input type={type} {...controlProps} />
      )}
      <p id={describedBy} role="alert" className="min-h-5 text-[13px] text-destructive">
        {error ?? ''}
      </p>
    </div>
  );
}
